import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const LEFT_DEFAULT = 320;
const LEFT_MIN = 240;
const LEFT_MAX = 560;
const RIGHT_DEFAULT = 300;
const RIGHT_MIN = 220;
const RIGHT_MAX = 520;
/** 拖拽释放时宽度小于此值则视为收起 */
const COLLAPSE_BELOW = 52;
const MAP_EDGE_GUTTER = 16;

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

export interface ResizableDashPanelsProps {
  left: React.ReactNode;
  right?: React.ReactNode;
  leftDefaultWidth?: number;
  /** 右侧浮层面板占用宽度 + 边距，供地图「返回全国」等按钮避让 */
  onChromeRightInsetPx?: (px: number) => void;
}

/**
 * 左右大屏面板：内缘拖拽调宽；拖至极窄或双击分割条可收起；边缘细条点击展开。
 */
export function ResizableDashPanels(
  props: ResizableDashPanelsProps,
): React.ReactElement {
  const { left, right, leftDefaultWidth = LEFT_DEFAULT, onChromeRightInsetPx } = props;

  const [leftW, setLeftW] = useState(LEFT_DEFAULT);
  const [rightW, setRightW] = useState(RIGHT_DEFAULT);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  const leftWRef = useRef(leftW);
  const rightWRef = useRef(rightW);
  leftWRef.current = leftW;
  rightWRef.current = rightW;

  const leftRestoreRef = useRef(LEFT_DEFAULT);
  const rightRestoreRef = useRef(RIGHT_DEFAULT);

  useEffect(() => {
    const nextWidth = clamp(leftDefaultWidth, LEFT_MIN, LEFT_MAX);
    leftRestoreRef.current = nextWidth;
    if (!leftCollapsed) {
      setLeftW(nextWidth);
    }
  }, [leftCollapsed, leftDefaultWidth]);

  const reportInset = useCallback(() => {
    const px = !right || rightCollapsed ? MAP_EDGE_GUTTER : rightW + MAP_EDGE_GUTTER;
    onChromeRightInsetPx?.(px);
  }, [right, rightCollapsed, rightW, onChromeRightInsetPx]);

  useLayoutEffect(() => {
    reportInset();
  }, [reportInset]);

  const endBodyResizeCursor = useCallback(() => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const startLeftResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (leftCollapsed) return;
      const startX = e.clientX;
      const startW = leftWRef.current;
      const dragW = { current: startW };
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';

      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        dragW.current = clamp(startW + dx, 40, LEFT_MAX);
        setLeftW(dragW.current);
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        endBodyResizeCursor();
        const final = dragW.current;
        if (final < COLLAPSE_BELOW) {
          leftRestoreRef.current = clamp(startW, LEFT_MIN, LEFT_MAX);
          setLeftCollapsed(true);
          setLeftW(leftRestoreRef.current);
        } else {
          leftRestoreRef.current = clamp(final, LEFT_MIN, LEFT_MAX);
          setLeftW(leftRestoreRef.current);
        }
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [leftCollapsed, endBodyResizeCursor],
  );

  const startRightResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (rightCollapsed) return;
      const startX = e.clientX;
      const startW = rightWRef.current;
      const dragW = { current: startW };
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';

      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        dragW.current = clamp(startW - dx, 40, RIGHT_MAX);
        setRightW(dragW.current);
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        endBodyResizeCursor();
        const final = dragW.current;
        if (final < COLLAPSE_BELOW) {
          rightRestoreRef.current = clamp(startW, RIGHT_MIN, RIGHT_MAX);
          setRightCollapsed(true);
          setRightW(rightRestoreRef.current);
        } else {
          rightRestoreRef.current = clamp(final, RIGHT_MIN, RIGHT_MAX);
          setRightW(rightRestoreRef.current);
        }
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [rightCollapsed, endBodyResizeCursor],
  );

  const collapseLeftByHandle = useCallback(() => {
    leftRestoreRef.current = clamp(leftWRef.current, LEFT_MIN, LEFT_MAX);
    setLeftCollapsed(true);
  }, []);

  const collapseRightByHandle = useCallback(() => {
    rightRestoreRef.current = clamp(rightWRef.current, RIGHT_MIN, RIGHT_MAX);
    setRightCollapsed(true);
  }, []);

  useEffect(
    () => () => {
      endBodyResizeCursor();
    },
    [endBodyResizeCursor],
  );

  return (
    <>
      {leftCollapsed ? (
        <button
          type="button"
          className="dash-panel-expand-tab dash-panel-expand-tab--left"
          aria-label="展开左侧菜单"
          title="展开左侧菜单"
          onClick={() => {
            setLeftCollapsed(false);
            setLeftW(leftRestoreRef.current);
          }}
        >
          <span className="dash-panel-expand-tab__chev" aria-hidden>
            ›
          </span>
        </button>
      ) : (
        <div
          className="dash-panel-outer dash-panel-outer--left"
          style={{ width: leftW }}
        >
          <div className="dash-panel-stack">
            <button
              type="button"
              className="dash-panel-collapse-btn"
              aria-label="收起左侧菜单"
              title="收起左侧菜单"
              onClick={collapseLeftByHandle}
            >
              收起
            </button>
            {left}
          </div>
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="调整左侧面板宽度，双击可隐藏"
            title="拖拽调整宽度；拖至极窄或双击可隐藏"
            className="dash-resize-handle dash-resize-handle--left-panel"
            onMouseDown={startLeftResize}
            onDoubleClick={(e) => {
              e.preventDefault();
              collapseLeftByHandle();
            }}
          />
        </div>
      )}

      {!right ? null : rightCollapsed ? (
        <button
          type="button"
          className="dash-panel-expand-tab dash-panel-expand-tab--right"
          aria-label="展开右侧详情"
          title="展开右侧详情"
          onClick={() => {
            setRightCollapsed(false);
            setRightW(rightRestoreRef.current);
          }}
        >
          <span className="dash-panel-expand-tab__chev" aria-hidden>
            ‹
          </span>
        </button>
      ) : (
        <div
          className="dash-panel-outer dash-panel-outer--right"
          style={{ width: rightW }}
        >
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="调整右侧面板宽度，双击可隐藏"
            title="拖拽调整宽度；拖至极窄或双击可隐藏"
            className="dash-resize-handle dash-resize-handle--right-panel"
            onMouseDown={startRightResize}
            onDoubleClick={(e) => {
              e.preventDefault();
              collapseRightByHandle();
            }}
          />
          <div className="dash-panel-stack">
            <button
              type="button"
              className="dash-panel-collapse-btn"
              aria-label="收起右侧详情"
              title="收起右侧详情"
              onClick={collapseRightByHandle}
            >
              收起
            </button>
            {right}
          </div>
        </div>
      )}
    </>
  );
}
